import * as React from 'react'
import { ReactElement, useEffect } from 'react'
import { Formik, useFormikContext } from 'formik'
import { RateDto } from '../../../config/fileConfig'
import { TierAction, UserBaseTierListProps } from './TierList'
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    makeStyles,
    shorthands,
    tokens,
} from '@fluentui/react-components'
import { DeleteRegular, DismissRegular, SaveRegular } from '@fluentui/react-icons'
import { BaseTierDto } from '../../../config/finance/financeConfig'
import { useTranslation } from 'react-i18next'

export type TierDialogProps<ParentT extends RateDto<TierT>, TierT extends BaseTierDto> = UserBaseTierListProps<
    ParentT,
    TierT
> & {
    show: boolean
    setOpen: (open: boolean) => void
    currentTier: TierT
    index: number
    action: TierAction
    onDismiss: () => void
    onSubmit: (index: number, tierValues: TierT) => void
    onDelete: (index: number) => void
}

const useStyles = makeStyles({
    container: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingHorizontalS, tokens.spacingVerticalS),
    },
})

function TierDialogContent<ParentT extends RateDto<TierT>, TierT extends BaseTierDto>(
    props: TierDialogProps<ParentT, TierT>,
): ReactElement {
    const { t: translate } = useTranslation()
    const { setValues, submitForm } = useFormikContext<TierT>()
    const styles = useStyles()

    useEffect(() => {
        setValues(props.currentTier)
    }, [props.currentTier])

    return (
        <>
            <DialogContent className={styles.container}>{props.createTierFormContainer(props)}</DialogContent>
            <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="transparent" icon={<DismissRegular />} onClick={props.onDismiss}>
                        {translate('action.cancel')}
                    </Button>
                </DialogTrigger>
                <Button icon={<SaveRegular />} appearance="primary" onClick={() => submitForm()}>
                    {translate('action.save')}
                </Button>
                {props.action === TierAction.EDIT && (
                    <Button
                        appearance="transparent"
                        icon={<DeleteRegular />}
                        onClick={() => {
                            props.onDelete(props.index)
                        }}
                    >
                        {translate('action.delete')}
                    </Button>
                )}
            </DialogActions>
        </>
    )
}

export default function TierDialog<ParentT extends RateDto<TierT>, TierT extends BaseTierDto>(
    props: TierDialogProps<ParentT, TierT>,
): ReactElement {
    const { t: translate } = useTranslation()

    return (
        <Dialog open={props.show} onOpenChange={props.onDismiss}>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>
                        {props.action === TierAction.EDIT
                            ? translate(`${props.labelPrefixKey}.edit`)
                            : translate(`${props.labelPrefixKey}.add`)}
                    </DialogTitle>
                    <Formik<TierT>
                        initialValues={props.currentTier}
                        onSubmit={values => props.onSubmit(props.index, values)}
                        validationSchema={props.validationSchema(translate)}
                    >
                        <TierDialogContent {...props} />
                    </Formik>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    )
}
